import React from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * One report table, described once and drawn twice.
 *
 * A report is a list of columns over a list of rows. On a laptop that is a
 * table; at a handset's width a nine-column table is a sideways scroll with
 * the number you came for off the edge of the screen. So each row becomes a
 * card: the column marked `phone: 'title'` names the row, `phone: 'sub'` sits
 * under it, and the rest read as label-over-value pairs two to a row. Nothing
 * is dropped — a report that hides a column on the phone is a different report.
 *
 * `sections` groups rows under a heading inside the one table, the way the
 * status board separates the floor from the lifecycle; `totals` closes the
 * table with a row (or a card) keyed by column.
 */

const cellClass = (col, row, py) => cn(
  'px-2.5', py,
  col.align === 'right' && 'text-right tabular-nums',
  col.mono && 'font-mono text-[11px]',
  typeof col.className === 'function' ? col.className(row) : col.className,
);

function CardList({ columns, groups, count, rowKey, empty, totals, testId }) {
  const titles = columns.filter((c) => c.phone === 'title');
  const title = titles.length ? titles : [columns[0]];
  const sub = columns.filter((c) => c.phone === 'sub');
  const rest = columns.filter((c) => !title.includes(c) && !sub.includes(c));
  const totalled = rest.filter((c) => totals && totals[c.key] !== undefined);

  return (
    <div className="space-y-2" data-testid={testId}>
      {count === 0 && (
        <p className="rounded-[10px] border border-slate-200 bg-white py-10 text-center text-xs text-slate-400">{empty}</p>
      )}
      {groups.map((group) => (
        <React.Fragment key={group.title || 'rows'}>
          {!!group.title && group.rows.length > 0 && (
            <p className="pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{group.title}</p>
          )}
          {group.rows.map((row) => (
            <div key={rowKey(row)} className="rounded-[10px] border border-slate-200 bg-white px-4 py-3" data-testid={`report-card-${rowKey(row)}`}>
              <div className="flex flex-wrap items-center gap-x-2 text-[14px] font-semibold text-slate-900">
                {title.map((c) => <span key={c.key} className="min-w-0">{c.render(row)}</span>)}
              </div>
              {sub.length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[12px] text-slate-500">
                  {sub.map((c) => <span key={c.key} className="min-w-0">{c.render(row)}</span>)}
                </div>
              )}
              {rest.length > 0 && (
                <dl className="mt-2.5 grid grid-cols-2 gap-x-3 gap-y-2">
                  {rest.map((c) => (
                    <div key={c.key} className="min-w-0">
                      <dt className="text-[10px] uppercase tracking-wider text-slate-400">{c.head}</dt>
                      <dd className="truncate text-[13px] tabular-nums text-slate-800">{c.render(row)}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>
          ))}
        </React.Fragment>
      ))}
      {totals && count > 0 && (
        <div className="rounded-[10px] border border-slate-300 bg-slate-100 px-4 py-3" data-testid="report-totals">
          <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-600">Total</p>
          <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
            {totalled.map((c) => (
              <div key={c.key} className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wider text-slate-400">{c.head}</dt>
                <dd className="truncate text-[13px] font-semibold tabular-nums text-slate-900">{totals[c.key]}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

export function Table({ columns, rows, sections, rowKey, empty, totals, dense = false, tone, testId }) {
  const isMobile = useIsMobile();
  const cols = columns.filter(Boolean);
  const groups = sections || [{ rows: rows || [] }];
  const count = groups.reduce((n, g) => n + g.rows.length, 0);

  if (isMobile) {
    return <CardList columns={cols} groups={groups} count={count} rowKey={rowKey} empty={empty} totals={totals} testId={testId} />;
  }

  const py = dense ? 'py-1.5' : 'py-2';
  const rowBorder = dense ? 'border-slate-100' : 'border-slate-200';
  const warn = tone === 'warn';
  // the total's label spans every leading column that has no figure of its own
  const lead = totals ? cols.findIndex((c) => totals[c.key] !== undefined) : 0;

  return (
    <div className={cn('overflow-x-auto rounded-lg border', warn ? 'border-amber-200' : 'border-slate-200')}>
      <table className="w-full text-xs" data-testid={testId}>
        <thead className={cn('text-[10px] uppercase tracking-wider', warn ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500')}>
          <tr>{cols.map((c) => <th key={c.key} className="px-2.5 py-2 text-left whitespace-nowrap">{c.head}</th>)}</tr>
        </thead>
        <tbody>
          {count === 0
            ? <tr><td colSpan={cols.length} className="px-2 py-10 text-center text-slate-400">{empty}</td></tr>
            : groups.map((group) => (
              <React.Fragment key={group.title || 'rows'}>
                {!!group.title && group.rows.length > 0 && (
                  <tr className="bg-slate-50">
                    <td colSpan={cols.length} className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                      {group.title}
                    </td>
                  </tr>
                )}
                {group.rows.map((row) => (
                  <tr key={rowKey(row)} className={cn('border-t', warn ? 'border-amber-100' : rowBorder)}>
                    {cols.map((c) => <td key={c.key} className={cellClass(c, row, py)}>{c.render(row)}</td>)}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          {totals && count > 0 && (
            <tr className="border-t-2 border-slate-300 bg-slate-100 font-semibold">
              <td className={cn('px-2.5', py)} colSpan={Math.max(lead, 1)}>Total</td>
              {cols.slice(Math.max(lead, 1)).map((c) => (
                <td key={c.key} className={cn('px-2.5 tabular-nums', py)}>{totals[c.key]}</td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Stat({ label, value, tone }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white p-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${tone || 'text-slate-800'}`}>{value}</p>
    </div>
  );
}

export const money = (v) => `₹${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
export const count = (v) => Number(v || 0).toLocaleString();
